import QRCode from 'qrcode';
import { QRCodeData } from '../types/models';

export class QRCodeUtils {
  /**
   * Generate QR code data URL for an inventory item
   */
  static async generateQRCode(itemData: QRCodeData): Promise<string> {
    try {
      const qrData = JSON.stringify(itemData);
      const dataUrl = await QRCode.toDataURL(qrData, {
        width: 256,
        margin: 2,
        errorCorrectionLevel: 'medium',
        color: { dark: '#000000', light: '#FFFFFF' }
      });
      return dataUrl;
    } catch (error) {
      console.error('Failed to generate QR code:', error);
      throw new Error('Failed to generate QR code');
    }
  }

  /**
   * Generate QR code as SVG string
   */
  static async generateQRCodeSVG(itemData: QRCodeData): Promise<string> {
    try {
      const qrData = JSON.stringify(itemData);
      const svg = await QRCode.toString(qrData, {
        type: 'svg' as const,
        width: 256,
        margin: 2,
        errorCorrectionLevel: 'medium' as const,
        color: { dark: '#000000', light: '#FFFFFF' }
      });
      return svg;
    } catch (error) {
      console.error('Failed to generate QR code SVG:', error);
      throw new Error('Failed to generate QR code SVG');
    }
  }

  /**
   * Parse QR code data to extract item information
   */
  static parseQRCodeData(qrData: string): QRCodeData | null {
    try {
      const parsed = JSON.parse(qrData) as QRCodeData;
      
      // Validate required fields
      if (!parsed.itemID || !parsed.itemName) {
        return null;
      }
      
      return parsed;
    } catch (error) {
      console.error('Error parsing QR code data:', error);
      return null;
    }
  }

  /**
   * Validate if a string contains valid QR code data for this app
   */
  static isValidQRCodeData(data: string): boolean {
    const parsed = this.parseQRCodeData(data);
    return parsed !== null;
  }

  /**
   * Generate a printable QR code with item information
   */
  static async generatePrintableQRCode(itemData: QRCodeData): Promise<string> {
    try {
      const qrDataUrl = await this.generateQRCode(itemData);
      
      // Create a canvas with QR code and text
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      canvas.width = 300;
      canvas.height = 350;

      // Fill white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            // Draw QR code
            ctx.drawImage(img, 22, 20, 256, 256);

            // Add text below QR code
            ctx.fillStyle = '#000000';
            ctx.font = '16px Arial';
            ctx.textAlign = 'center';
            
            // Item name
            ctx.font = 'bold 18px Arial';
            ctx.fillText(itemData.itemName, canvas.width / 2, 300);
            
            // Item ID
            ctx.font = '12px Arial';
            ctx.fillText(`ID: ${itemData.itemID}`, canvas.width / 2, 320);
            
            // Organization (if present)
            if (itemData.organizationID) {
              ctx.fillText(`Org: ${itemData.organizationID}`, canvas.width / 2, 335);
            }

            resolve(canvas.toDataURL());
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = reject;
        img.src = qrDataUrl;
      });
    } catch (error) {
      console.error('Error generating printable QR code:', error);
      throw new Error('Failed to generate printable QR code');
    }
  }

  /**
   * Download QR code as image file
   */
  static downloadQRCode(dataUrl: string, filename: string): void {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Print QR code
   */
  static printQRCode(dataUrl: string, itemName: string): void {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      throw new Error('Unable to open print window');
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Print QR Code - ${itemName}</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              display: flex;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              font-family: Arial, sans-serif;
            }
            .qr-container {
              text-align: center;
            }
            img {
              max-width: 300px;
              height: auto;
            }
            h2 {
              margin-top: 10px;
              font-size: 18px;
            }
            @media print {
              body { margin: 0; }
              .qr-container { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="qr-container">
            <img src="${dataUrl}" alt="QR Code for ${itemName}" />
            <h2>${itemName}</h2>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    
    // Wait for image to load before printing
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }
}